import { supabase } from '@/integrations/supabase/client';

export interface VendorVisibilityQuestionnaireSummary {
  blueprintId: string;
  slug: string;
  title: string;
  topic?: string | null;
}

export interface VendorVisibilityAccessGrant {
  id: string;
  organizationId: string;
  organizationName: string;
  organizationLogoUrl: string | null;
  organizationDomain: string | null;
  scope: 'all' | 'selected';
  status: 'active' | 'revoked';
  grantedAt: string;
  sharedQuestionnaireCount: number;
  sharedQuestionnaires: VendorVisibilityQuestionnaireSummary[];
}

export interface VendorVisibilityAccessRequest {
  id: string;
  organizationId: string;
  organizationName: string;
  organizationLogoUrl: string | null;
  organizationDomain: string | null;
  requestScope: 'all' | 'selected';
  requestMessage: string | null;
  requestedAt: string;
  requestedQuestionnaireCount: number;
  requestedQuestionnaires: VendorVisibilityQuestionnaireSummary[];
}

export interface VendorVisibilitySharedQuestionnaire {
  blueprintId: string;
  slug: string;
  title: string;
  topic: string | null;
  submissionId: string;
  submissionVersion: number;
  submittedAt: string | null;
  activeShareCount: number;
}

export interface VendorVisibilityPublishedQuestionnaire {
  id: string;
  slug: string;
  title: string;
  topic: string | null;
}

export interface VendorQuestionnaireVisibilityResult {
  autoGrantAccess: boolean;
  organizationsWithAccess: VendorVisibilityAccessGrant[];
  pendingRequests: VendorVisibilityAccessRequest[];
  sharedQuestionnaires: VendorVisibilitySharedQuestionnaire[];
  publishedQuestionnaires: VendorVisibilityPublishedQuestionnaire[];
}

export function getVendorQuestionnaireVisibilityQueryKey(vendorProfileId: string | null | undefined) {
  return ['vendor-questionnaire-visibility', vendorProfileId ?? 'none'] as const;
}

type RpcResponse<T> = Promise<{ data: T | null; error: { message: string } | null }>;

function getRpc() {
  return ((functionName: string, args: Record<string, unknown>) =>
    supabase.rpc(functionName as never, args as never)) as unknown as (
      functionName: string,
      args: Record<string, unknown>,
    ) => RpcResponse<any>;
}

export async function fetchVendorQuestionnaireVisibility(vendorProfileId: string) {
  const rpc = getRpc();
  const { data, error } = await rpc('get_vendor_questionnaire_visibility', {
    p_vendor_profile_id: vendorProfileId,
  });

  if (error) {
    throw new Error(error.message || 'Failed to load supplier questionnaire visibility');
  }

  return (data ?? {
    autoGrantAccess: false,
    organizationsWithAccess: [],
    pendingRequests: [],
    sharedQuestionnaires: [],
    publishedQuestionnaires: [],
  }) as VendorQuestionnaireVisibilityResult;
}

export async function upsertVendorQuestionnaireShareGrant(params: {
  vendorProfileId: string;
  organizationId: string;
  scope: 'all' | 'selected';
  blueprintIds?: string[];
  responseMessage?: string | null;
}) {
  const rpc = getRpc();
  const { data, error } = await rpc('upsert_vendor_questionnaire_share_grant', {
    p_vendor_profile_id: params.vendorProfileId,
    p_organization_id: params.organizationId,
    p_scope: params.scope,
    p_blueprint_ids: params.blueprintIds ?? [],
    p_response_message: params.responseMessage ?? null,
  });

  if (error) {
    throw new Error(error.message || 'Failed to update supplier questionnaire sharing');
  }

  return data;
}

export async function respondVendorQuestionnaireAccessRequest(params: {
  requestId: string;
  approve: boolean;
  scope?: 'all' | 'selected' | null;
  blueprintIds?: string[];
  responseMessage?: string | null;
}) {
  const rpc = getRpc();
  const { data, error } = await rpc('respond_vendor_questionnaire_access_request', {
    p_request_id: params.requestId,
    p_approve: params.approve,
    p_scope: params.scope ?? null,
    p_blueprint_ids: params.blueprintIds ?? null,
    p_response_message: params.responseMessage ?? null,
  });

  if (error) {
    throw new Error(error.message || 'Failed to respond to supplier questionnaire access request');
  }

  return data;
}

export async function revokeVendorQuestionnaireShareGrant(params: {
  shareGrantId: string;
  revokeReason?: string | null;
}) {
  const rpc = getRpc();
  const { data, error } = await rpc('revoke_vendor_questionnaire_share_grant', {
    p_share_grant_id: params.shareGrantId,
    p_revoke_reason: params.revokeReason ?? null,
  });

  if (error) {
    throw new Error(error.message || 'Failed to revoke supplier questionnaire sharing');
  }

  return data;
}
